/**
 * Captain's Opportunities — a NON-LINEAR "what can I do right now?" board.
 *
 * WHY (retention Nº1): brand-new captains reach the open sea and freeze — "e agora? onde?". The Helm Coach
 * (coach.ts) teaches the CONTROLS, but the moment it ends there is no answer to "what else is there?". A real
 * player in chat had hoisted chests and asked "onde vendo?" — the game's shape was opaque even to a veteran.
 *
 * DESIGN (owner's call, explicit): this is NOT a locked questline. Sea of Solana is a sandbox FFA — "cada um
 * por si". A rigid "do X, then Y, then Z" ladder would FUNNEL the game and fight its spirit. So this is a
 * COMPASS OF POSSIBILITIES: every activity the sea offers, listed SIMULTANEOUSLY with its reward and — the
 * bit the lost player needed — WHERE to do it. The captain sees the whole leque and CHOOSES. We may softly
 * HIGHLIGHT a few "for you now" (you're carrying loot → cash in; you can afford a bigger hull → upgrade) but
 * we never HIDE an option and never force an order.
 *
 * Numbers are pulled from the shared config (never invented): a sold chest is GOLD_PER_CHEST, a kill is
 * GOLD_PER_KILL, the Brigantine costs SHIP_UNLOCK_COST[BRIGANTINE], founding an Armada costs CLAN.CREATE_COST.
 *
 * Client-only, NO WIRE — every signal it reads already rides the snapshot self-header / S_PROFILE / S_CLAN.
 * The pure DATA + suggestion logic live here (DOM-free, locked by opportunities.test.ts); the collapsible
 * board is the class at the bottom.
 */
import { CLAN, ECONOMY, MARAUDER, SHIP_UNLOCK_COST, ShipClassId } from '@piratas/protocol';
import { injectParchmentCss } from './parchment.js';

/** Confirmed costs (single source of truth — config, so copy can never drift from the sim). */
export const BRIGANTINE_COST = SHIP_UNLOCK_COST[ShipClassId.BRIGANTINE]; // 1500 doubloons
export const CLAN_CREATE_COST = CLAN.CREATE_COST;                        // 1000 doubloons

/** Live state the board reads to decide which options to gently SUGGEST (never to hide any). All of it is
 *  already known client-side with no new wire (snapshot self-header + S_PROFILE + S_CLAN). */
export interface OppState {
  carrying: boolean;         // a treasure chest is aboard → suggest cashing in
  holdCount: number;         // gathered materials aboard → suggest selling / crafting
  gold: number;              // carried, UNBANKED loot (dropped on sink) → suggest cashing in
  doubloons: number;         // banked soft currency (buys hulls, upgrades, founding a clan)
  kills: number;             // lifetime kills (this profile)
  shipClass: number;         // current ShipClassId (0 = Sloop, the starter)
  brigantineUnlocked: boolean; // the Brigantine (or a bigger hull) already owned
  inClan: boolean;           // already flying an Armada's colours
}

/** One activity on the board. `suggest` (optional) decides if it deserves a "for you now" glow this frame —
 *  a pure function of the live state. It NEVER removes the row; the whole list is always rendered. */
export interface Opportunity {
  id: string;
  icon: string;
  title: string;   // the verb — what you DO
  reward: string;  // what you GET (real numbers where fixed)
  where: string;   // the answer the lost player needed — WHERE to do it
  suggest?: (s: OppState) => boolean;
}

/** At most this many rows glow "for you now" at once — a nudge, not a checklist takeover. */
export const MAX_SUGGESTED = 3;

/**
 * The full leque — ALWAYS shown in full. Order is the reading order; suggested rows float to the top of the
 * render but are never added to or removed from this set based on state.
 */
export const OPPORTUNITIES: Opportunity[] = [
  {
    id: 'cashin', icon: '🪙', title: 'Cash in your loot',
    reward: `Sell a chest for ${ECONOMY.GOLD_PER_CHEST}g · bank your gold`,
    where: 'Any outpost ring (chests) · your home dock (gold)',
    suggest: (s) => s.carrying || s.holdCount > 0 || s.gold > 0,
  },
  {
    id: 'dig', icon: '🗺', title: 'Hunt & dig for treasure',
    reward: `Chests ${ECONOMY.GOLD_PER_CHEST}g+ · buried hoards & relics`,
    where: '◆ glints on the chart · X-marked far isles',
  },
  {
    id: 'sink', icon: '⚔️', title: 'Sink rival captains',
    reward: `+${ECONOMY.GOLD_PER_KILL}g a kill (${MARAUDER.GOLD_PER_MARAUDER}g marauders) + Renown`,
    where: 'The open sea — hunt the red blips',
  },
  {
    id: 'gather', icon: '🎣', title: 'Fish, dive wrecks & run storms',
    reward: 'Materials to sell at market & craft with',
    where: '🎣 teal shoals · sunken wrecks · storm fronts',
    suggest: (s) => s.holdCount > 0,
  },
  {
    id: 'craft', icon: '⚒', title: 'Craft gear & level a trade',
    reward: 'Special ammo, grog, repair kits + profession XP',
    where: 'Your base · the Tavern & Foundry',
  },
  {
    id: 'raid', icon: '🏴', title: 'Raid a rival base',
    reward: 'Plunder their stockpiled doubloons',
    where: "Sail into an enemy's base waters",
  },
  {
    id: 'upgrade', icon: '⚓', title: 'Upgrade your ship & base',
    reward: `Bigger hull (Brigantine ${BRIGANTINE_COST}) · armor · cargo · guns`,
    where: 'The shipyard at your home dock',
    suggest: (s) => (s.shipClass === ShipClassId.SLOOP && !s.brigantineUnlocked && s.doubloons >= BRIGANTINE_COST),
  },
  {
    id: 'armada', icon: '🤝', title: 'Join or found an Armada',
    reward: `Allies, a treasury & territory (found for ${CLAN_CREATE_COST})`,
    where: 'The Armada tab in your log',
    suggest: (s) => !s.inClan && s.doubloons >= CLAN_CREATE_COST,
  },
  {
    id: 'casino', icon: '🎲', title: 'Chance the tavern casino',
    reward: 'Gamble doubloons — slots, blackjack, poker',
    where: 'The Capital port',
  },
];

/**
 * PURE: which option ids to glow "for you now", given the live state. A SUBSET of OPPORTUNITIES (never adds
 * unknown ids, never removes rows), capped at MAX_SUGGESTED, in board order (stable — no flicker). Returns []
 * when nothing stands out, and the untouched full leque is the guide. This is a nudge layer, not a filter.
 */
export function suggestedOpportunities(s: OppState): string[] {
  const out: string[] = [];
  for (const o of OPPORTUNITIES) {
    if (o.suggest?.(s)) out.push(o.id);
    if (out.length >= MAX_SUGGESTED) break;
  }
  return out;
}

/* ------------------------------------------------------------------------------------------------------ */
/* The board (DOM). Owner reproved the old dark-glass panel on two counts: (a) generic "AI" look, off the    */
/* game's parchment chart language, and (b) it hogged screen space. So the board is now a discreet HUD       */
/* ICON — a rolled scroll ("Captain's Orders") — that OPENS a CONTAINED parchment panel (never full-screen); */
/* a tap outside, the ✕, or Esc closes it. Self-contained on document.body (like the coach cards) so it never   */
/* touches the fragile #hud grid. The SANDBOX BRAIN is untouched: the whole leque is always rendered, and    */
/* `suggestedOpportunities` (the pure, tested nudge layer) still decides the ≤3 "for you now" glows — only    */
/* the packaging changed.                                                                                    */
/* ------------------------------------------------------------------------------------------------------ */

export class Opportunities {
  private btn: HTMLElement | null = null;      // the rolled-scroll HUD icon
  private modal: HTMLElement | null = null;    // the contained parchment panel (backdrop + sheet)
  private active = false;                      // gated on: no teaching layer up + alive world
  private open = false;                        // the panel is showing
  private lastKey = '';                        // memo of the last render (suggested-set + open) to skip work
  private mounted = false;
  private onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape' && this.open) this.close(); };

  constructor(private readonly host: HTMLElement, private readonly sfx: { ui: () => void } = { ui: () => {} }) {}

  /** Show/hide the whole feature (the HUD icon). main gates this so it never fights the Helm Coach for a first-timer. */
  setActive(on: boolean): void {
    if (on === this.active) return;
    this.active = on;
    if (on) { this.ensure(); this.btn!.classList.add('on'); }
    else { if (this.btn) this.btn.classList.remove('on'); this.close(); }
  }

  /** Feed the live state each frame; refresh the icon pip + (if open) the panel, only when the set changes. */
  update(s: OppState): void {
    if (!this.active) return;
    this.ensure();
    this.lastState = s;                       // remembered so an on-demand open paints the freshest leque
    const sug = suggestedOpportunities(s);   // the SAME pure nudge layer — untouched
    const key = sug.join(',') + '|' + (this.open ? 'o' : 'c');
    if (key === this.lastKey) return;
    this.lastKey = key;
    // icon pip: a discreet gilt count of how many activities are "for you now" (presentation of the same set)
    const pip = this.btn!.querySelector('.op-pip') as HTMLElement;
    if (pip) { pip.textContent = String(sug.length); pip.classList.toggle('show', sug.length > 0); }
    this.btn!.classList.toggle('nudge', sug.length > 0);
    if (this.open) this.render(new Set(sug));
  }

  private toggle(): void { if (this.open) this.close(); else this.openPanel(); }

  private openPanel(): void {
    this.ensure();
    this.open = true; this.lastKey = '';
    this.modal!.classList.add('open');
    this.render(new Set(suggestedOpportunities(this.readState())));
    this.sfx.ui();
    document.addEventListener('keydown', this.onKey);
  }

  private close(): void {
    if (!this.open) return;
    this.open = false; this.lastKey = '';
    if (this.modal) this.modal.classList.remove('open');
    document.removeEventListener('keydown', this.onKey);
  }

  /** The panel opens on demand (a tap), between `update` frames — capture the freshest suggestion set so the
   *  first paint already reflects the live state. Reads the same OppState the icon pip was last fed. */
  private lastState: OppState = { carrying: false, holdCount: 0, gold: 0, doubloons: 0, kills: 0, shipClass: ShipClassId.SLOOP, brigantineUnlocked: false, inClan: false };
  private readState(): OppState { return this.lastState; }

  private render(suggested: Set<string>): void {
    const body = this.modal!.querySelector('.op-body') as HTMLElement;
    // suggested rows float to the top (stable order), the rest follow — the WHOLE leque, always. (unchanged)
    const rows = [...OPPORTUNITIES].sort((a, b) => {
      const sa = suggested.has(a.id) ? 0 : 1, sb = suggested.has(b.id) ? 0 : 1;
      if (sa !== sb) return sa - sb;
      return OPPORTUNITIES.indexOf(a) - OPPORTUNITIES.indexOf(b);
    });
    body.innerHTML = rows.map((o) => {
      const hot = suggested.has(o.id);
      return `<div class="op-row${hot ? ' hot' : ''}">` +
        `<span class="op-ic">${o.icon}</span>` +
        `<div class="op-tx"><b>${o.title}${hot ? '<em>· for you now</em>' : ''}</b>` +
        `<span class="op-rw">${o.reward}</span>` +
        `<span class="op-wh">📍 ${o.where}</span></div></div>`;
    }).join('') +
      // the chart key — the POI legend, so a marker on the minimap is never a mystery
      `<div class="op-key"><b>⚓ Chart key</b>` +
      `<span><i class="k-port"></i>Port — sell &amp; step ashore</span>` +
      `<span><i class="k-cap"></i>★ Capital — market, casino, auction</span>` +
      `<span><i class="k-home"></i>Your base — bank &amp; upgrade</span>` +
      `<span><i class="k-fish"></i>Fishing shoal</span>` +
      `<span><i class="k-chest"></i>◆ Treasure waters</span>` +
      `<span><i class="k-foe"></i>Rival captain</span></div>`;
  }

  private ensure(): void {
    if (this.mounted) return;
    this.mounted = true;
    injectParchmentCss(); // shared chart/parchment kit (.pg-frame / .pg-paper) — one paper generator, casa com a carta
    if (!document.getElementById('op-style')) {
      const st = document.createElement('style'); st.id = 'op-style';
      st.textContent = `
        /* ── the HUD ICON: a rolled scroll ("Captain's Orders"), aged brass, on the LEFT band clear of the
              coach cards and the helm (bottom-left). PvP-first: one small tap target, never over a control. */
        #opps-btn{ position:fixed; left:calc(env(safe-area-inset-left,0px) + 10px); top:calc(env(safe-area-inset-top,8px) + 200px);
          z-index:23; width:48px; height:48px; border-radius:12px; box-sizing:border-box; pointer-events:auto; cursor:pointer;
          display:flex; align-items:center; justify-content:center; opacity:0; transform:translateY(-6px);
          transition:opacity .3s ease, transform .3s ease, filter .14s ease; font-family:'Cinzel',Georgia,serif;
          background:radial-gradient(circle at 50% 32%, #f4da92 0%, #cba043 44%, #7a5416 78%, #4a3016 100%);
          border:1px solid rgba(40,24,6,.7);
          box-shadow:0 8px 22px rgba(0,6,12,.55), inset 0 1px 0 rgba(255,246,214,.5), inset 0 -3px 6px rgba(50,30,8,.5); }
        #opps-btn.on{ opacity:1; transform:translateY(0); }
        #opps-btn:active{ transform:scale(.94); }
        #opps-btn .op-scroll{ font-size:24px; line-height:1; filter:drop-shadow(0 1px 1px rgba(50,30,8,.7)); }
        /* a discreet gilt count of "for you now" nudges — presentation of the pure suggested set */
        #opps-btn .op-pip{ position:absolute; top:-5px; right:-5px; min-width:17px; height:17px; padding:0 4px; box-sizing:border-box;
          border-radius:9px; display:none; align-items:center; justify-content:center; font-family:system-ui,sans-serif;
          font-size:10px; font-weight:800; color:#3a1010; background:linear-gradient(180deg,#ffd979,#e0a63a);
          border:1px solid rgba(90,26,26,.6); box-shadow:0 1px 3px rgba(0,0,0,.5); }
        #opps-btn .op-pip.show{ display:flex; }
        #opps-btn.nudge{ animation:opNudge 2.6s ease-in-out infinite; }
        @keyframes opNudge{ 0%,100%{ box-shadow:0 8px 22px rgba(0,6,12,.55), inset 0 1px 0 rgba(255,246,214,.5), inset 0 -3px 6px rgba(50,30,8,.5), 0 0 0 0 rgba(232,180,90,.5); }
          60%{ box-shadow:0 8px 22px rgba(0,6,12,.55), inset 0 1px 0 rgba(255,246,214,.5), inset 0 -3px 6px rgba(50,30,8,.5), 0 0 0 8px rgba(232,180,90,0); } }

        /* ── the CONTAINED panel: a framed parchment sheet (NOT full-screen), centred over a dim backdrop.
              A tap on the backdrop, the ✕, or Esc closes it. The sheet scrolls INSIDE itself (overflow contained). */
        #opps-modal{ position:fixed; inset:0; z-index:64; display:none; align-items:center; justify-content:center;
          padding:calc(env(safe-area-inset-top,8px) + 12px) 14px calc(env(safe-area-inset-bottom,0px) + 14px);
          background:rgba(4,10,16,.62); pointer-events:auto; }
        #opps-modal.open{ display:flex; animation:opFade .18s ease-out; }
        @keyframes opFade{ from{ opacity:0; } to{ opacity:1; } }
        #opps-panel{ width:min(94vw,392px); max-height:100%; display:flex; flex-direction:column;
          animation:opRise .22s cubic-bezier(.2,.8,.3,1); }
        @keyframes opRise{ from{ opacity:0; transform:translateY(12px) scale(.98); } to{ opacity:1; transform:none; } }
        #opps-panel .pg-paper{ display:flex; flex-direction:column; min-height:0; max-height:100%; padding:11px 12px 12px; }
        #opps-panel .op-hd{ display:flex; align-items:center; gap:9px; padding:1px 1px 8px; }
        #opps-panel .op-hd .op-crest{ font-size:19px; line-height:1; filter:drop-shadow(0 1px 1px rgba(90,60,20,.5)); }
        #opps-panel .op-hd h3{ margin:0; font-size:13.5px; }
        #opps-panel .op-hd .op-sub2{ font-size:9.5px; color:#6a4c22; font-style:italic; margin-top:1px; }
        #opps-panel .op-x{ margin-left:auto; width:30px; height:30px; flex:0 0 auto; border-radius:7px; cursor:pointer; pointer-events:auto;
          display:flex; align-items:center; justify-content:center; font-size:16px; font-weight:800; color:#4a2e0c; font-family:'Cinzel',Georgia,serif;
          background:linear-gradient(180deg, rgba(222,186,102,.5), rgba(150,110,40,.32)); border:1px solid rgba(90,60,16,.55);
          box-shadow:inset 0 1px 0 rgba(255,246,214,.5); }
        #opps-panel .op-x:active{ transform:scale(.92); }
        #opps-panel .op-body{ overflow-y:auto; -webkit-overflow-scrolling:touch; padding-right:2px; min-height:0; }

        /* rows — inked parchment entries, an ink neat-line between them; the icon in a small brass roundel */
        #opps-panel .op-row{ display:flex; align-items:flex-start; gap:10px; padding:8px 3px; border-top:1px solid rgba(58,40,16,.22); }
        #opps-panel .op-row:first-child{ border-top:0; }
        #opps-panel .op-row.hot{ background:linear-gradient(90deg, rgba(224,178,74,.22), rgba(224,178,74,.05)); border-radius:5px;
          box-shadow:inset 0 0 0 1px rgba(150,110,40,.42); padding-left:7px; padding-right:7px; }
        #opps-panel .op-ic{ font-size:19px; line-height:1.15; flex:0 0 auto; width:28px; height:28px; border-radius:50%;
          display:flex; align-items:center; justify-content:center;
          background:radial-gradient(circle at 40% 34%, rgba(244,218,146,.6), rgba(150,110,40,.24));
          border:1px solid rgba(90,60,16,.4); box-shadow:inset 0 1px 0 rgba(255,246,214,.4); filter:drop-shadow(0 1px 1px rgba(90,60,20,.35)); }
        #opps-panel .op-tx{ display:flex; flex-direction:column; gap:2px; min-width:0; }
        #opps-panel .op-tx b{ font-family:'Cinzel',Georgia,serif; font-size:13px; font-weight:900; color:#3b2712; letter-spacing:.01em; text-shadow:0 1px 0 rgba(255,248,225,.45); }
        #opps-panel .op-tx b em{ font-style:italic; font-weight:700; font-size:9.5px; letter-spacing:.02em; color:#8a5a12; margin-left:6px; text-transform:none; }
        #opps-panel .op-rw{ font-size:11px; font-weight:600; color:#5a4020; line-height:1.36; }
        #opps-panel .op-wh{ font-size:10.5px; font-weight:700; color:#3f6a2a; line-height:1.36; }
        /* chart key — a boxed legend on a faint inset, ink swatches ringed like chart symbols */
        #opps-panel .op-key{ margin-top:9px; padding:9px 10px; border-radius:6px; background:rgba(90,60,20,.08);
          box-shadow:inset 0 0 0 1px rgba(58,40,16,.32); display:flex; flex-direction:column; gap:4px; }
        #opps-panel .op-key b{ font-family:'Cinzel',Georgia,serif; font-size:10px; font-weight:900; letter-spacing:.1em; color:#5a3a12; text-transform:uppercase; margin-bottom:2px; }
        #opps-panel .op-key span{ display:flex; align-items:center; gap:8px; font-size:10.5px; font-weight:600; color:#5a4020; }
        #opps-panel .op-key i{ width:11px; height:11px; border-radius:50%; flex:0 0 auto; box-shadow:0 0 0 1px rgba(58,40,16,.55); }
        #opps-panel .op-key i.k-port{ background:#4a8f5c; } #opps-panel .op-key i.k-cap{ background:#e0b04a; }
        #opps-panel .op-key i.k-home{ background:#c8901e; } #opps-panel .op-key i.k-fish{ background:#3a9a90; }
        #opps-panel .op-key i.k-chest{ background:#e0a63a; border-radius:2px; } #opps-panel .op-key i.k-foe{ background:#a83030; }`;
      document.head.appendChild(st);
    }
    // the HUD icon
    const btn = document.createElement('div'); btn.id = 'opps-btn';
    btn.setAttribute('role', 'button'); btn.setAttribute('aria-label', "Captain's Orders — what can I do?");
    btn.innerHTML = `<span class="op-scroll">📜</span><span class="op-pip"></span>`;
    btn.addEventListener('pointerdown', (e) => { e.preventDefault(); this.toggle(); });
    this.host.appendChild(btn);
    this.btn = btn;
    // the contained parchment panel (hidden until opened)
    const modal = document.createElement('div'); modal.id = 'opps-modal';
    modal.innerHTML =
      `<div id="opps-panel" class="pg-frame"><div class="pg-paper">` +
      `<div class="op-hd"><span class="op-crest">📜</span><div><h3 class="pg-title">Captain's Orders</h3>` +
      `<div class="op-sub2">the whole sea is yours — pick your course</div></div>` +
      `<div class="op-x" role="button" aria-label="Close">✕</div></div>` +
      `<hr class="pg-rule"/>` +
      `<div class="op-body"></div></div></div>`;
    // tap the backdrop (but not the panel) to close
    modal.addEventListener('pointerdown', (e) => { if (e.target === modal) this.close(); });
    (modal.querySelector('.op-x') as HTMLElement).addEventListener('pointerdown', (e) => { e.preventDefault(); e.stopPropagation(); this.close(); });
    this.host.appendChild(modal);
    this.modal = modal;
  }
}
